import { useEffect, useState } from 'react';
import {
  getShippingFees,
  type ApiClient,
  type ShippingFees,
  type ShippingFeesParams,
} from '../api';

export interface UseShippingFeesResult {
  fees: ShippingFees | null;
  /** True while a quote is in flight — including the debounce window — so the
   *  UI can show loaders in place of the numbers. */
  loading: boolean;
}

// As the delivery page settles, the item set, the address and the chosen
// service each flip a dependency a few ms apart — without debouncing that
// fires three identical requests. Wait for the inputs to stop changing, then
// fetch once.
const DEBOUNCE_MS = 250;

/**
 * Fetches the pre-checkout shipping fees for the given items — debounced, and
 * re-quoted whenever the ids, the delivery address, or the chosen shipping
 * options actually change. `params.addressId` unlocks the sales-tax quote and
 * `params.serviceIds` the shipping fee; omit them (reveal / disclaimer) and
 * those stay "TBD". Returns `{ fees, loading }`: `fees` is null until the first
 * request resolves (and stays null on failure, so callers fall back to their
 * placeholders); `loading` is true while a request is pending.
 */
export function useShippingFees(
  client: ApiClient,
  poolItemIds: (number | string)[],
  params: ShippingFeesParams = {},
): UseShippingFeesResult {
  const [fees, setFees] = useState<ShippingFees | null>(null);
  const [loading, setLoading] = useState(true);

  // Stable dependencies: re-fetch only when the actual values change, not on
  // every new array/object identity a parent render produces.
  const key = poolItemIds.join(',');
  const addressId = params.addressId ?? null;
  const serviceKey = (params.serviceIds ?? []).join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      const ids = key ? key.split(',') : [];
      const serviceIds = serviceKey ? serviceKey.split(',') : [];
      void getShippingFees(client, ids, { addressId, serviceIds })
        .then((result) => {
          if (cancelled) return;
          setFees(result);
          setLoading(false);
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, key, addressId, serviceKey]);

  return { fees, loading };
}
